import axios from 'axios';
import { apiService } from './api';
import { Dataset } from '../types';

// Use the same API base URL configuration as the main API service
const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_URL || '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Types for Pathfinder API
export interface PathfinderDevice {
  id: string;
  type: 'watch' | 'vehicle' | 'phone';
  name?: string;
  active: boolean;
  color?: string;
  lastSeen?: Date;
  batteryLevel?: number;
  coordinates?: [number, number];
}

export interface PathfinderStudent {
  id: string;
  name: string;
  devices: PathfinderDevice[];
  course: string;
  status: 'active' | 'inactive';
}

export interface PathfinderDataPoint {
  id: string;
  deviceId: string;
  deviceType?: 'watch' | 'vehicle' | 'phone';
  studentId: string;
  timestamp: Date;
  coordinates: [number, number];
  accuracy: number;
  activity?: string;
  metadata?: Record<string, any>;
  _geometry?: any;
  _geometryType?: string;
  _fileFormat?: string;
  _originalIndex?: number;
}

export interface PathfinderRoute {
  id: string;
  studentId: string;
  deviceId: string;
  deviceType: 'watch' | 'vehicle' | 'phone';
  points: PathfinderDataPoint[];
  coordinates: [number, number][]; // Array of [lat, lng] for line drawing
  startTime: Date;
  endTime: Date;
  totalDistance: number; // in meters
  color: string; // Color for visualization
}

export interface PathfinderQuery {
  dateRange: [Date, Date];
  studentIds?: string[];
  deviceTypes?: string[];
  selectedCourse?: string; // Course ID for ArcGIS context
  selectedDatabase?: string; // Database tag for filtering
  spatialFilter?: {
    center: [number, number];
    radius: number; // in meters
  };
  timeFilter?: {
    startTime: string; // HH:MM format
    endTime: string;   // HH:MM format
  };
  limit?: number;
}

export interface TimeWindowQuery {
  fullDateRange: [Date, Date];
  windowDateRange: [Date, Date];
  studentIds: string[];
  deviceTypes?: string[];
  selectedCourse?: string;
  selectedDatabase?: string;
}

export interface CoTravelerEncounter {
  timestamp: Date;
  location: [number, number];
  duration: number; // in minutes
  proximity: number; // in meters
  confidence: number; // 0-1 confidence score
  activity?: string; // dwell, transit, etc.
  locationName?: string; // human-readable location
}

export interface CoTravelerResult {
  primaryDevice: string;
  coTravelerDevice: string;
  encounters: CoTravelerEncounter[];
  totalEncounters: number;
  totalDuration: number;
  averageProximity: number;
  confidenceScore: number;
  temporalOverlap: number; // percentage of time overlap
  spatialOverlap: number; // percentage of spatial overlap
  frequentLocations: Array<{
    location: [number, number];
    name?: string;
    encounters: number;
    totalDuration: number;
  }>;
}

export interface CoTravelerAnalysisParams {
  deviceIds: string[];
  dateRange: [Date, Date];
  proximityThreshold: number; // meters
  timeThreshold: number; // minutes
  minEncounters: number;
  minDuration: number; // minutes
  courseId?: string; // Course ID for database context (like SET.NET 8)
  databaseTag?: string; // Database tag/name to keep queries in correct database
}

export interface PatternOfLifeResult {
  deviceId: string;
  patterns: {
    digital: {
      callActivity: Array<{ hour: number; count: number }>;
      smsActivity: Array<{ hour: number; count: number }>;
      webActivity: Array<{ hour: number; count: number }>;
    };
    physical: {
      dwellLocations: Array<{
        location: [number, number];
        address?: string;
        visits: number;
        totalDuration: number; // in minutes
        averageDuration: number;
      }>;
      movementPatterns: Array<{
        fromLocation: [number, number];
        toLocation: [number, number];
        frequency: number;
        averageTime: number; // in minutes
      }>;
    };
  };
}

// Note type definition
export interface EntityNote {
  id: number;
  content: string;
  created_by: string;
  created_at: string;
  watchlist_id?: number;
  geofence_id?: number;
}

// Watchlist entry with all its notes (optimized endpoint)
export interface WatchlistWithNotes {
  id: number;
  registration_id: string;
  label?: string;
  notes?: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
  entity_notes: EntityNote[];
}

// Note attachment type definition
export interface NoteAttachment {
  id: number;
  note_id: number;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string;
  uploaded_at: string;
  description?: string;
  download_url: string;
  is_image: boolean;
}

// Document processing result
export interface DocumentProcessingResult {
  extracted_text: string | null;
  insights: string | null;
  success: boolean;
  error: string | null;
}

// Append insights result
export interface AppendInsightsResult {
  success: boolean;
  message: string;
  note_id: number;
  insights: string;
}

class PathfinderApiService {
  public client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 120000, // Increased to 2 minutes for large datasets
  });

  constructor() {
    // Add authentication interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  // Get available datasets (for compatibility with existing code)
  async getDatasets(): Promise<Dataset[]> {
    try {
      const datasets = await apiService.getDatasets();
      return datasets;
    } catch (error) {
      return [];
    }
  }

  // Method: Query timeExtent data (replicates 's server-side filtering)
  async queryTimeExtent(query: TimeWindowQuery): Promise<PathfinderDataPoint[]> {
    try {
      
      // TODO: Implement PostgreSQL version of time-extent endpoint
      // For now, use regular query with time filtering
      const fallbackQuery: PathfinderQuery = {
        dateRange: query.windowDateRange,
        studentIds: query.studentIds,
        selectedCourse: query.selectedCourse,
        deviceTypes: query.deviceTypes,
        limit: undefined
      };
      return await this.queryDataPoints(fallbackQuery);
      
    } catch (error) {
      throw new Error('Time window query failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  // Query data points (use dedicated Pathfinder endpoint for direct GPS data)
  async queryDataPoints(query: PathfinderQuery): Promise<PathfinderDataPoint[]> {
    try {
      
      // Allow course-wide queries (no students) for co-traveler analysis
      
      // Use the dedicated Pathfinder endpoint for direct GPS data querying
      const requestPayload = {
        // Only send students if provided; backend treats None as all-entities-in-course
        students: query.studentIds && query.studentIds.length > 0 ? query.studentIds : undefined,
        start_date: query.dateRange[0].toISOString(),
        end_date: query.dateRange[1].toISOString(),
        course_id: query.selectedCourse, // Pass the selected course ID
        database_tag: query.selectedDatabase, // Pass the selected database tag
        device_types: query.deviceTypes,
        limit: query.limit  // No default limit - load all data
      };
      
      // Use PostgreSQL-based endpoint for direct database queries
      const response = await this.client.post('/pathfinder-postgres/postgres-query', requestPayload);
      
      const rawDataPoints = response.data;
      
      // Debug: Show raw backend response structure
      if (rawDataPoints.length === 0) {
      }
      
      // Debug: Show structure of first data point
      if (rawDataPoints.length > 0) {
      }
      
      // Convert backend response to PathfinderDataPoint format
      const dataPoints: PathfinderDataPoint[] = rawDataPoints.map((point: any, index: number) => {
        // Debug first few points
        if (index < 3) {
        }
        
        // Handle coordinates - Internal format: [latitude, longitude]
        let coordinates: [number, number];
        if (point.coordinates && Array.isArray(point.coordinates) && point.coordinates.length >= 2) {
          // Assume incoming coordinates are [lat, lng] internal format
          coordinates = [parseFloat(point.coordinates[0]), parseFloat(point.coordinates[1])];
        } else if (point.latitude !== undefined && point.longitude !== undefined) {
          // Convert from separate lat/lon fields to internal [lat, lng]
          coordinates = [parseFloat(point.latitude), parseFloat(point.longitude)];
        } else {
          coordinates = [0, 0]; // Default fallback
        }
        
        return {
          id: point.id || `point-${index}`,
          deviceId: point.device_id || `${point.student_id}-${point.device_type || 'phone'}`,
          deviceType: point.device_type || 'phone',
          studentId: point.student_id || point.entity_id,
          timestamp: new Date(point.timestamp),
          coordinates: coordinates,
          accuracy: point.accuracy,
          activity: point.activity,
          metadata: {
            altitude: point.altitude,
            speed: point.speed,
            bearing: point.bearing,
            course_id: point.course_id
          }
        };
      });
      
      return dataPoints;
      
    } catch (error) {
      throw new Error(`OSINT Explorer API query failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }   

  // Pattern of Life analysis (use real dwell and route data)
  async analyzePatternOfLife(
    deviceId: string,
    dateRange: [Date, Date]
  ): Promise<PatternOfLifeResult> {
    try {
      // Get data points for the device
      const dataPoints = await this.queryDataPoints({
        dateRange,
        studentIds: [deviceId],
        limit: undefined  // No limit - load all data
      });
      
      // Analyze physical patterns from dwell locations
      const dwellPoints = dataPoints.filter(p => p.activity === 'dwell');
      const dwellLocationMap = new Map<string, {
        location: [number, number];
        visits: number;
        totalDuration: number;
        address?: string;
      }>();
      
      dwellPoints.forEach(point => {
        const key = point.coordinates[0].toFixed(4) + '_' + point.coordinates[1].toFixed(4);
        if (!dwellLocationMap.has(key)) {
          dwellLocationMap.set(key, {
            location: point.coordinates,
            visits: 0,
            totalDuration: 0,
            address: point.metadata?.placeName || 'Unknown Location'
          });
        }
        const dwell = dwellLocationMap.get(key)!;
        dwell.visits++;
        dwell.totalDuration += point.metadata?.totalDuration || 30; // Default 30 minutes
      });
      
      // Convert to array and calculate averages
      const dwellLocations = Array.from(dwellLocationMap.values()).map(dwell => ({
        ...dwell,
        averageDuration: dwell.totalDuration / dwell.visits
      }));
      
      // Analyze movement patterns
      const movementPoints = dataPoints.filter(p => p.activity === 'movement').sort((a, b) => 
        a.timestamp.getTime() - b.timestamp.getTime()
      );
      
      const movementPatterns: PatternOfLifeResult['patterns']['physical']['movementPatterns'] = [];
      for (let i = 0; i < movementPoints.length - 1; i++) {
        const from = movementPoints[i];
        const to = movementPoints[i + 1];
        const timeDiff = (to.timestamp.getTime() - from.timestamp.getTime()) / (1000 * 60); // minutes
        
        if (timeDiff > 0 && timeDiff < 120) { // Only consider movements within 2 hours
          movementPatterns.push({
            fromLocation: from.coordinates,
            toLocation: to.coordinates,
            frequency: 1,
            averageTime: timeDiff
          });
        }
      }
      
      // Generate hourly activity patterns based on data points
      const hourlyActivity = Array.from({ length: 24 }, (_, hour) => {
        const count = dataPoints.filter(p => p.timestamp.getHours() === hour).length;
        return { hour, count };
      });
      
      return {
        deviceId,
        patterns: {
          digital: {
            callActivity: hourlyActivity.map(h => ({ ...h, count: Math.floor(h.count * 0.3) })),
            smsActivity: hourlyActivity.map(h => ({ ...h, count: Math.floor(h.count * 0.5) })),
            webActivity: hourlyActivity.map(h => ({ ...h, count: Math.floor(h.count * 0.8) }))
          },
          physical: {
            dwellLocations,
            movementPatterns
          }
        }
      };
    } catch (error) {
      return {
        deviceId,
        patterns: {
          digital: {
            callActivity: [],
            smsActivity: [],
            webActivity: []
          },
          physical: {
            dwellLocations: [],
            movementPatterns: []
          }
        }
      };
    }
  }

  // Search functionality (search through real analysis data)
  async searchData(searchTerm: string, filters?: Partial<PathfinderQuery>): Promise<PathfinderDataPoint[]> {
    try {
      // Get all available data points
      const allDataPoints = await this.queryDataPoints({
        dateRange: filters?.dateRange || [new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()],
        studentIds: filters?.studentIds,
        deviceTypes: filters?.deviceTypes,
        limit: undefined  // No limit - load all data
      });
      
      // Filter data points based on search term
      const searchLower = searchTerm.toLowerCase();
      const filteredPoints = allDataPoints.filter(point => {
        return (
          point.activity?.toLowerCase().includes(searchLower) ||
          point.metadata?.placeName?.toLowerCase().includes(searchLower) ||
          point.deviceId.toLowerCase().includes(searchLower) ||
          point.id.toLowerCase().includes(searchLower)
        );
      });
      
      return filteredPoints;
    } catch (error) {
      return [];
    }
  }

  // Generate route lines from data points for visualization, separated by device type
  generateRoutes(dataPoints: PathfinderDataPoint[]): PathfinderRoute[] {
    
    // Group points by student ID and device type
    const deviceGroups: Record<string, PathfinderDataPoint[]> = {};
    
    dataPoints.forEach(point => {
      const deviceKey = (point.studentId || point.deviceId) + '-' + (point.deviceType || 'unknown');
      if (!deviceGroups[deviceKey]) {
        deviceGroups[deviceKey] = [];
      }
      deviceGroups[deviceKey].push(point);
    });

    const routes: PathfinderRoute[] = [];
    
    // Device type colors matching style
    const deviceTypeColors = {
      watch: '#2196f3',    // Blue
      vehicle: '#9c27b0',  // Purple
      phone: '#4caf50',    // Green
      unknown: '#757575'   // Gray
    };

    Object.entries(deviceGroups).forEach(([deviceKey, points]) => {
      if (points.length < 2) return; // Need at least 2 points for a route

      // Extract student ID and device type from key
      // deviceKey format: "Student-07-vehicle"
      const parts = deviceKey.split('-');
      const studentId = parts.slice(0, -1).join('-'); // "Student-07"
      const deviceTypeStr = parts[parts.length - 1]; // "vehicle"
      const deviceType = deviceTypeStr as 'watch' | 'vehicle' | 'phone';
      const originalDeviceId = points[0].deviceId;
      const uniqueDeviceId = studentId + '-' + deviceType;

      // Sort points by timestamp
      const sortedPoints = points.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      
      // Calculate total distance
      let totalDistance = 0;
      const coordinates: [number, number][] = sortedPoints.map(point => point.coordinates);
      
      for (let i = 1; i < coordinates.length; i++) {
        totalDistance += this.calculateDistance(
          coordinates[i-1][0], coordinates[i-1][1],
          coordinates[i][0], coordinates[i][1]
        );
      }

      const route: PathfinderRoute = {
        id: studentId + '-' + deviceType + '-' + originalDeviceId,
        studentId,
        deviceId: uniqueDeviceId,
        deviceType,
        points: sortedPoints,
        coordinates,
        startTime: sortedPoints[0].timestamp,
        endTime: sortedPoints[sortedPoints.length - 1].timestamp,
        totalDistance,
        color: deviceTypeColors[deviceType] || deviceTypeColors.unknown
      };

      routes.push(route);
    });

    return routes;
  }

  // Helper function to extract device type from device ID
  extractDeviceType(deviceId: string): 'watch' | 'vehicle' | 'phone' {
    if (deviceId.includes('-watch')) return 'watch';
    if (deviceId.includes('-vehicle')) return 'vehicle';
    if (deviceId.includes('-phone')) return 'phone';
    // Default fallback
    return 'phone';
  }

  // Helper function to extract student ID from device ID
  extractStudentId(deviceId: string): string {
    // Remove device type suffix to get student ID
    return deviceId.replace(/-watch|-vehicle|-phone|_device/g, '');
  }

  // Calculate distance between two points in meters (Haversine formula)
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }

  // Dwell data functionality - query dwell locations
  async getDwells(params: {
    courseId?: string;
    deviceType?: string;
    days?: number;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
    studentsOnly?: boolean;
  }): Promise<any[]> {
    try {
      
      const queryParams: any = {
        course_id: params.courseId,
        device_type: params.deviceType || 'all',
        limit: params.limit || 100,
      };
      
      // Filter to Students only if requested
      if (params.studentsOnly) {
        queryParams.entity_type = 'Student';
      }
      
      // Use date range if provided, otherwise use days
      if (params.startDate && params.endDate) {
        queryParams.start_date = params.startDate.toISOString();
        queryParams.end_date = params.endDate.toISOString();
      } else {
        queryParams.days = params.days || 7;
      }
      
      const response = await this.client.get('/cop-dwells', {
        params: queryParams
      });
      
      
      // Return the dwells array from the response
      return response.data.dwells || [];
    } catch (error: any) {
      throw new Error(`Failed to fetch dwell data: ${error.response?.data?.message || error.message}`);
    }
  }

  // Area search functionality - query devices within a circular area or polygon
  async queryAreaSearch(params: {
    center?: [number, number]; // [lat, lon] - required for circles
    radius?: number; // meters - required for circles
    polygon?: number[][][]; // GeoJSON polygon coordinates - for polygon search
    dateRange: [Date, Date];
    selectedCourse?: string;
    excludeStudentIds?: string[]; // Students to exclude from results
    studentsOnly?: boolean; // Filter to Students only (exclude RP, SRP)
  }): Promise<PathfinderDataPoint[]> {
    try {
      // Format dates without milliseconds (GeoNeutron API requirement)
      const startDateFormatted = params.dateRange[0].toISOString().replace(/\.\d{3}Z$/, 'Z');
      const endDateFormatted = params.dateRange[1].toISOString().replace(/\.\d{3}Z$/, 'Z');
      
      let payload: any = {
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        registration_ids: [] // Empty array to search all devices
      };
      
      // Add search area parameters based on type
      if (params.polygon) {
        payload.polygon = params.polygon;
      } else if (params.center && params.radius) {
        payload.center_lat = params.center[0];
        payload.center_lon = params.center[1];
        payload.radius_meters = params.radius;
      } else {
        throw new Error('Either polygon or center+radius must be provided');
      }
      
      
      const response = await this.client.post('/geoneutron/area-search', payload);
      
      
      // Extract data points from GeoNeutron response
      const dataPoints = response.data.devices || [];
      
      // Transform response to PathfinderDataPoint format
      return dataPoints.map((point: any, _: number) => {
        // Handle coordinates - Internal format: [latitude, longitude]
        let coordinates: [number, number];
        if (point.latitude !== undefined && point.longitude !== undefined) {
          // Convert from separate lat/lon fields to internal [lat, lng] format
          coordinates = [parseFloat(point.latitude), parseFloat(point.longitude)];
        } else if (point.coordinates && Array.isArray(point.coordinates) && point.coordinates.length >= 2) {
          // Assume existing coordinates are in [lat, lng] internal format
          coordinates = [parseFloat(point.coordinates[0]), parseFloat(point.coordinates[1])];
        } else {
          coordinates = [0, 0]; // Default fallback
        }
        
        return {
          id: point.id || `${point.registration_id}-${point.timestamp}`,
          studentId: point.registration_id,
          deviceId: point.registration_id,
          deviceType: 'mobile',
          coordinates, // Internal format: [lat, lng] - converted to GeoJSON [lng, lat] when rendering
          timestamp: new Date(point.timestamp),
          accuracy: point.accuracy,
          activity: point.activity,
          metadata: {
            altitude: point.altitude,
            speed: point.speed,
            bearing: point.bearing,
            registration_id: point.registration_id
          }
        };
      });

    } catch (error: any) {
      throw new Error(`Area search failed: ${error.response?.data?.message || error.message}`);
    }
  }

  // GeoNeutron area search - enrich with external device tracking data
  async queryGeoNeutronAreaSearch(params: {
    center?: [number, number]; // [lat, lon] - required for circles
    radius?: number; // meters - required for circles
    polygon?: number[][][]; // GeoJSON polygon coordinates
    dateRange: [Date, Date];
    registrationIds?: string[]; // GeoNeutron registration IDs
  }): Promise<any> {
    try {
      
      const startDateFormatted = params.dateRange[0].toISOString().replace(/\.\d{3}Z$/, 'Z');
      const endDateFormatted = params.dateRange[1].toISOString().replace(/\.\d{3}Z$/, 'Z');

      let payload: any = {
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        registration_ids: params.registrationIds || [],
        response_type: 'DIRECT'
      };
      
      // Add search area parameters based on type
      if (params.polygon) {
        payload.polygon = params.polygon;
      } else if (params.center && params.radius) {
        payload.center_lat = params.center[0];
        payload.center_lon = params.center[1];
        payload.radius_meters = params.radius;
      } else {
        throw new Error('Either polygon or center+radius must be provided');
      }

      console.log('[GeoNeutron] Area search START:', {
        center: params.center,
        radius: params.radius,
        dateRange: `${startDateFormatted} - ${endDateFormatted}`,
        timestamp: new Date().toISOString()
      });
      
      const startTime = Date.now();
      
      // Use 5-minute timeout for area searches (large areas can take 2+ minutes)
      const response = await this.client.post('/geoneutron/area-search', payload, {
        timeout: 300000, // 5 minutes - busy airports can take 2+ minutes
        maxContentLength: Infinity, // Allow large responses (can be 16MB+)
        maxBodyLength: Infinity
      });

      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log('[GeoNeutron] Area search COMPLETE:', {
        total_devices: response.data?.total_devices,
        unique_registration_ids: response.data?.unique_registration_ids,
        elapsed_seconds: elapsed,
        response_size_approx: JSON.stringify(response.data).length
      });

      return response.data;
    } catch (error: any) {
      // Log the error for debugging
      const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
      const isNetworkError = error?.message?.includes('Network Error');
      console.error('[GeoNeutron] Area search FAILED:', {
        message: error?.message,
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        isTimeout,
        isNetworkError,
        timestamp: new Date().toISOString()
      });
      
      // Build descriptive error message
      let errorMsg = 'Unknown error';
      if (isTimeout) {
        errorMsg = 'Request timed out (5 min limit) - try a smaller date range or radius';
      } else if (isNetworkError) {
        errorMsg = 'Network error - check your connection';
      } else if (error?.response?.status === 504) {
        errorMsg = 'Gateway timeout - server took too long to respond';
      } else if (error?.response?.status === 502) {
        errorMsg = 'Bad gateway - server may be restarting';
      } else if (error?.message) {
        errorMsg = error.message;
      }
      
      // Don't throw - return empty so main search can continue
      return {
        total_devices: 0,
        unique_registration_ids: 0,
        devices: [],
        search_params: {},
        source: 'geoneutron',
        error: errorMsg
      };
    }
  }

  // Query multiple areas with GeoNeutron (intersection or union)
  async queryGeoNeutronMultiAreaSearch(params: {
    areas: Array<{
      center: [number, number]; // [lat, lon]
      radius: number; // meters
      id: string;
    }>;
    polygons?: Array<{
      id: string;
      coordinates: number[][][]; // GeoJSON polygon coordinates
    }>;
    dateRange: [Date, Date];
    useAndLogic: boolean; // true = intersection (common), false = union (all)
    registrationIds?: string[];
  }): Promise<any> {
    try {
      const startDateFormatted = params.dateRange[0].toISOString().replace(/\.\d{3}Z$/, 'Z');
      const endDateFormatted = params.dateRange[1].toISOString().replace(/\.\d{3}Z$/, 'Z');

      const payload: any = {
        areas: params.areas.map(area => ({
          latitude: area.center[0],
          longitude: area.center[1],
          radius: Math.floor(area.radius),
          id: area.id
        })),
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        use_and_logic: params.useAndLogic,
        registration_ids: params.registrationIds || [],
        response_type: 'DIRECT'
      };

      // Add polygons if provided
      if (params.polygons && params.polygons.length > 0) {
        payload.polygons = params.polygons.map(poly => ({
          id: poly.id,
          coordinates: poly.coordinates
        }));
      }

      // CRITICAL: Log the exact use_and_logic value being sent

      // Calculate dynamic timeout based on date range and number of areas
      const daysDiff = Math.ceil((params.dateRange[1].getTime() - params.dateRange[0].getTime()) / (1000 * 60 * 60 * 24));
      const numAreas = params.areas.length + (params.polygons?.length || 0);
      const baseTimeout = 120000; // 2 minutes base
      const timeoutPerDayPerArea = 10000; // 10 seconds per day per area
      const dynamicTimeout = Math.min(baseTimeout + (daysDiff * numAreas * timeoutPerDayPerArea), 600000); // Cap at 10 minutes
      

      const response = await this.client.post('/geoneutron/multi-area-search', payload, {
        timeout: dynamicTimeout
      });

      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // DISCOVER-FIRST SMART SEARCH
  // Uses device count discovery to optimize search strategy for populated areas
  async queryGeoNeutronDiscoverFirst(params: {
    areas?: Array<{
      center: [number, number]; // [lat, lon]
      radius: number; // meters
      id: string;
    }>;
    polygons?: Array<{
      id: string;
      coordinates: number[][][]; // GeoJSON polygon coordinates
    }>;
    dateRange: [Date, Date];
    registrationIds?: string[];
    sampleDays?: number; // Days to sample for discovery (default 3)
    onProgress?: (progress: number, message: string) => void;
  }): Promise<any> {
    try {
      const startDateFormatted = params.dateRange[0].toISOString().replace(/\.\d{3}Z$/, 'Z');
      const endDateFormatted = params.dateRange[1].toISOString().replace(/\.\d{3}Z$/, 'Z');

      const payload: any = {
        areas: (params.areas || []).map(area => ({
          latitude: area.center[0],
          longitude: area.center[1],
          radius: Math.floor(area.radius),
          id: area.id
        })),
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        registration_ids: params.registrationIds || [],
        sample_days: params.sampleDays || 3
      };

      // Add polygons if provided
      if (params.polygons && params.polygons.length > 0) {
        payload.polygons = params.polygons.map(poly => ({
          id: poly.id,
          coordinates: poly.coordinates
        }));
      }

      console.log('[GeoNeutron] Discover-First search START:', {
        areas: params.areas?.length || 0,
        polygons: params.polygons?.length || 0,
        dateRange: `${startDateFormatted} - ${endDateFormatted}`,
        sampleDays: params.sampleDays || 3,
        timestamp: new Date().toISOString()
      });

      const startTime = Date.now();

      // Use longer timeout since this endpoint handles its own partitioning
      const response = await this.client.post('/geoneutron/discover-first-search', payload, {
        timeout: 600000, // 10 minutes - handles complex partitioned queries
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });

      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log('[GeoNeutron] Discover-First search COMPLETE:', {
        total_devices: response.data?.total_devices,
        unique_registration_ids: response.data?.unique_registration_ids,
        strategy_used: response.data?.performance?.strategy_used,
        discovery_count: response.data?.performance?.discovery_device_count,
        elapsed_seconds: elapsed
      });

      return response.data;
    } catch (error: any) {
      const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
      console.error('[GeoNeutron] Discover-First search FAILED:', {
        message: error?.message,
        isTimeout,
        timestamp: new Date().toISOString()
      });

      // Return empty result on failure
      return {
        total_devices: 0,
        unique_registration_ids: 0,
        devices: [],
        search_params: {},
        source: 'geoneutron-discover-first',
        performance: {},
        error: isTimeout ? 'Request timed out' : error?.message
      };
    }
  }

  // 🔍 DISCOVER ONLY - Get device count estimate without full search
  async discoverGeoNeutronDeviceCount(params: {
    areas?: Array<{
      center: [number, number];
      radius: number;
      id: string;
    }>;
    polygons?: Array<{
      id: string;
      coordinates: number[][][];
    }>;
    dateRange: [Date, Date];
    sampleDays?: number;
  }): Promise<{
    discovery: {
      sample_device_count: number;
      sample_signal_count: number;
      estimated_total_devices: number;
      sample_days: number;
      total_days: number;
      discovery_time_seconds?: number;
    };
    recommended_strategy: {
      name: string;
      reason: string;
      chunk_days: number;
      max_concurrent: number;
      use_device_batching?: boolean;
    };
  }> {
    try {
      const startDateFormatted = params.dateRange[0].toISOString().replace(/\.\d{3}Z$/, 'Z');
      const endDateFormatted = params.dateRange[1].toISOString().replace(/\.\d{3}Z$/, 'Z');

      const payload: any = {
        areas: (params.areas || []).map(area => ({
          latitude: area.center[0],
          longitude: area.center[1],
          radius: Math.floor(area.radius),
          id: area.id
        })),
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        sample_days: params.sampleDays || 3
      };

      if (params.polygons && params.polygons.length > 0) {
        payload.polygons = params.polygons.map(poly => ({
          id: poly.id,
          coordinates: poly.coordinates
        }));
      }

      const response = await this.client.post('/geoneutron/discover-only', payload, {
        timeout: 60000 // 1 minute for discovery only
      });

      return response.data;
    } catch (error: any) {
      console.error('[GeoNeutron] Discovery failed:', error?.message);
      // Return defaults on failure
      return {
        discovery: {
          sample_device_count: 0,
          sample_signal_count: 0,
          estimated_total_devices: 0,
          sample_days: 0,
          total_days: 0
        },
        recommended_strategy: {
          name: 'temporal_aggressive',
          reason: 'Discovery failed - using aggressive fallback',
          chunk_days: 3,
          max_concurrent: 6
        }
      };
    }
  }

  // Get pattern of life for specific GeoNeutron devices
  // Automatically chunks requests to stay within API limit (100 devices max)
  async getGeoNeutronPatternOfLife(params: {
    registrationIds: string[];
    daysBack?: number;
    startDate?: string;
    endDate?: string;
    areaCenter?: [number, number];
    areaRadius?: number;
    onProgress?: (completed: number, total: number, message: string) => void;
  }): Promise<any> {
    const BATCH_SIZE = 100; // API limit
    const allIds = params.registrationIds;
    const totalDevices = allIds.length;
    
    // If under limit, make single request
    if (totalDevices <= BATCH_SIZE) {
      return this._makePolRequest(params);
    }
    
    // Chunk into batches of 100
    console.log(`[PoL API] Chunking ${totalDevices} devices into ${Math.ceil(totalDevices / BATCH_SIZE)} batches`);
    
    const allLocations: any[] = [];
    const allDwells: any[] = [];
    const deviceStats: Record<string, any> = {};
    let successfulBatches = 0;
    let failedBatches = 0;
    
    for (let i = 0; i < totalDevices; i += BATCH_SIZE) {
      const batchIds = allIds.slice(i, i + BATCH_SIZE);
      const batchNum = Math.floor(i / BATCH_SIZE) + 1;
      const totalBatches = Math.ceil(totalDevices / BATCH_SIZE);
      
      if (params.onProgress) {
        params.onProgress(i, totalDevices, `Processing batch ${batchNum}/${totalBatches}...`);
      }
      
      console.log(`[PoL API] Batch ${batchNum}/${totalBatches}: ${batchIds.length} devices`);
      
      try {
        const batchResult = await this._makePolRequest({
          ...params,
          registrationIds: batchIds
        });
        
        // Merge results
        if (batchResult.locations) {
          allLocations.push(...batchResult.locations);
        }
        if (batchResult.dwells) {
          allDwells.push(...batchResult.dwells);
        }
        if (batchResult.device_stats) {
          Object.assign(deviceStats, batchResult.device_stats);
        }
        
        successfulBatches++;
      } catch (error: any) {
        console.error(`[PoL API] Batch ${batchNum} failed:`, error.message);
        failedBatches++;
        // Continue with other batches
      }
      
      // Small delay between batches to be nice to the API
      if (i + BATCH_SIZE < totalDevices) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    if (params.onProgress) {
      params.onProgress(totalDevices, totalDevices, 'Complete!');
    }
    
    console.log(`[PoL API] Complete: ${successfulBatches} successful, ${failedBatches} failed batches`);
    console.log(`[PoL API] Results: ${allLocations.length} locations, ${allDwells.length} dwells`);
    
    return {
      locations: allLocations,
      dwells: allDwells,
      device_stats: deviceStats,
      batch_info: {
        total_devices: totalDevices,
        successful_batches: successfulBatches,
        failed_batches: failedBatches
      }
    };
  }
  
  // Internal method to make a single PoL request (max 100 devices)
  private async _makePolRequest(params: {
    registrationIds: string[];
    daysBack?: number;
    startDate?: string;
    endDate?: string;
    areaCenter?: [number, number];
    areaRadius?: number;
  }): Promise<any> {
    try {
      const payload: any = {
        registration_ids: params.registrationIds,
      };
      
      // If specific dates are provided, use them. Otherwise use days_back
      if (params.startDate && params.endDate) {
        payload.start_date = params.startDate;
        payload.end_date = params.endDate;
      } else {
        payload.days_back = params.daysBack || 30;
      }
      
      // Add geographic filtering if provided
      if (params.areaCenter && params.areaRadius) {
        payload.area_center = params.areaCenter;
        payload.area_radius = params.areaRadius;
      }
      
      // Calculate dynamic timeout based on number of devices and date range
      const numDevices = params.registrationIds.length;
      const baseTimeout = 180000; // 3 minutes base
      const timeoutPerDevice = 15000; // 15 seconds per device
      const dynamicTimeout = Math.min(baseTimeout + (numDevices * timeoutPerDevice), 600000); // Cap at 10 minutes
      
      const response = await this.client.post('/geoneutron/pattern-of-life', payload, {
        timeout: dynamicTimeout
      });
      
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Enrich internal area search with GeoNeutron data
  async enrichAreaSearchWithGeoNeutron(
    internalDeviceIds: string[],
    center: [number, number],
    radius: number,
    dateRange: [Date, Date]
  ): Promise<any> {
    try {

      const response = await this.client.post('/geoneutron/enrich-area-search', {
        internal_device_ids: internalDeviceIds,
        center_lat: center[0],
        center_lon: center[1],
        radius_meters: radius,
        start_date: dateRange[0].toISOString(),
        end_date: dateRange[1].toISOString()
      });

      return response.data;
    } catch (error: any) {
      return {
        enriched: false,
        error: error.message
      };
    }
  }

  // Generate AI analysis for area search results
  async generateAreaAnalysisAI(params: {
    areaName: string;
    center: [number, number];
    radius: number;
    entitiesCount: number;
    dwellInsights: any[];
    coLocationInsights: any[];
    mapboxContext?: any;
    useFullDetail?: boolean;
  }): Promise<{ success: boolean; summary: string; raw_data: any }> {
    try {
      if (params.mapboxContext) {
      }
      
      const response = await this.client.post('/pathfinder/area-analysis-ai', {
        areaName: params.areaName,
        center: params.center,
        radius: params.radius,
        entitiesCount: params.entitiesCount,
        dwellInsights: params.dwellInsights,
        coLocationInsights: params.coLocationInsights,
        mapboxContext: params.mapboxContext,
        useFullDetail: params.useFullDetail
      });

      return response.data;
    } catch (error: any) {
      throw new Error(`AI analysis failed: ${error.response?.data?.message || error.message}`);
    }
  }

  // Generate AI analysis for Story Mode timeline
  async generateStoryModeAI(params: {
    entities: string[];
    totalEvents: number;
    travelCount: number;
    dwellCount: number;
    observationCount?: number;
    totalTravelDistance: number;
    totalTravelTime: number;
    totalDwellTime: number;
    uniqueLocations: string[];
    uniqueObservationLocations?: string[];
    infrastructureProximity?: Array<{ type: string; name: string; distance_meters: number }>;
    timelineEvents: any[];
    // New fields for enhanced analysis
    repeatedVisits?: Array<{ location: string; visitCount: number; totalDuration: number; entities: string[] }>;
    coLocations?: Array<{ location: string; entities: string[]; overlapCount: number; totalOverlapTime: number }>;
  }): Promise<{ success: boolean; summary: string }> {
    try {
      
      const response = await this.client.post('/pathfinder/story-mode-ai', {
        entities: params.entities,
        totalEvents: params.totalEvents,
        travelCount: params.travelCount,
        dwellCount: params.dwellCount,
        observationCount: params.observationCount || 0,
        totalTravelDistance: params.totalTravelDistance,
        totalTravelTime: params.totalTravelTime,
        totalDwellTime: params.totalDwellTime,
        uniqueLocations: params.uniqueLocations,
        uniqueObservationLocations: params.uniqueObservationLocations || [],
        infrastructureProximity: params.infrastructureProximity || [],
        timelineEvents: params.timelineEvents,
        // Include repeated visits and co-locations for enhanced analysis
        repeatedVisits: params.repeatedVisits || [],
        coLocations: params.coLocations || []
      });

      return response.data;
    } catch (error: any) {
      throw new Error(`Story Mode AI analysis failed: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get Digital Pattern of Life data
   */
  async getDigitalPoL(params: {
    course_id?: string;
    entity_ids?: string;
    days?: number;
    activity_type?: string;
    limit?: number;
  }): Promise<any[]> {
    try {
      return await apiService.getDigitalPoL(params);
    } catch (error: any) {
      throw error;
    }
  }

  // Generate AI analysis for Digital PoL data
  async generateDigitalPoLAI(params: {
    entities: string[];
    totalActivities: number;
    activityBreakdown: Record<string, number>;
    entityBreakdown: Record<string, number>;
    uniqueEntities: number;
    dateRange: { start: string; end: string };
    topApps: Array<{ name: string; count: number; totalTime: number }>;
    topUrls: Array<{ url: string; count: number }>;
    topCallContacts: Array<{ contact: string; count: number; totalDuration: number }>;
    topSmsContacts: Array<{ contact: string; count: number }>;
    peakHour: number | null;
    course: string;
    timespan: number;
  }): Promise<{ success: boolean; summary: string }> {
    try {
      
      const response = await this.client.post('/pathfinder/digital-pol-ai', {
        entities: params.entities,
        totalActivities: params.totalActivities,
        activityBreakdown: params.activityBreakdown,
        entityBreakdown: params.entityBreakdown,
        uniqueEntities: params.uniqueEntities,
        dateRange: params.dateRange,
        topApps: params.topApps,
        topUrls: params.topUrls,
        topCallContacts: params.topCallContacts,
        topSmsContacts: params.topSmsContacts,
        peakHour: params.peakHour,
        course: params.course,
        timespan: params.timespan,
      });

      return response.data;
    } catch (error: any) {
      throw new Error(`Digital PoL AI analysis failed: ${error.response?.data?.detail || error.message}`);
    }
  }

  // === Watchlist API ===
  
  async addToWatchlist(registrationId: string, name?: string, notes?: string, tags?: string[], courseId?: string): Promise<any> {
    try {
      const response = await this.client.post('/watchlist', {
        registration_id: registrationId,
        name: name || `Device ${registrationId.slice(0, 8)}`,
        notes: notes || '',
        tags: tags || [],
        course_id: courseId
      });
      
      return response.data;
    } catch (error: any) {
      if (error.response) {
      }
      throw error;
    }
  }

  async getWatchlist(courseId?: string): Promise<any[]> {
    try {
      const params: any = { active_only: true };
      if (courseId) {
        params.course_id = courseId;
      }
      
      // Add timestamp to prevent caching
      const requestParams = { ...params, _t: new Date().getTime() };
      const response = await this.client.get('/watchlist', { params: requestParams });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Optimized: Get all watchlist entries with their notes in a single query
   * This is much faster than getWatchlist + getNotes for each device
   */
  async getWatchlistWithNotes(): Promise<WatchlistWithNotes[]> {
    try {
      const requestParams = { active_only: true, _t: new Date().getTime() };
      const response = await this.client.get('/watchlist/with-notes', { params: requestParams });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async removeFromWatchlist(deviceId: number): Promise<void> {
    try {
      await this.client.delete(`/watchlist/${deviceId}`);
    } catch (error: any) {
      throw error;
    }
  }

  async updateWatchlistDevice(
    deviceId: number,
    updates: { name?: string; notes?: string; tags?: string[]; active?: boolean }
  ): Promise<any> {
    const response = await this.client.patch(`/watchlist/${deviceId}`, updates);
    return response.data;
  }

  // === Notes API ===
  
  async getNotes(params: { watchlist_id?: number, geofence_id?: number }): Promise<EntityNote[]> {
    try {
      // Add timestamp to prevent caching
      const requestParams = { ...params, _t: new Date().getTime() };
      const response = await this.client.get('/notes', { params: requestParams });
      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  async createNote(content: string, watchlist_id?: number, geofence_id?: number): Promise<EntityNote> {
    try {
      const response = await this.client.post('/notes', {
        content,
        watchlist_id,
        geofence_id
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async deleteNote(noteId: number): Promise<void> {
    try {
      await this.client.delete(`/notes/${noteId}`);
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update a note's content
   */
  async updateNote(noteId: number, content: string): Promise<EntityNote> {
    try {
      const response = await this.client.put(`/notes/${noteId}`, { content });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update a note's title with discovered location information
   */
  async updateNoteTitle(
    noteId: number, 
    newTitle: string,
    locationInfo?: {
      placeName?: string;
      city?: string;
      country?: string;
    }
  ): Promise<EntityNote> {
    try {
      const response = await this.client.patch(`/notes/${noteId}/update-title`, {
        new_title: newTitle,
        new_location_name: locationInfo?.placeName,
        new_city: locationInfo?.city,
        new_country: locationInfo?.country
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // === Note Attachments API ===

  async uploadNoteAttachment(
    noteId: number,
    file: File,
    description?: string
  ): Promise<NoteAttachment> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }
      
      const response = await this.client.post(`/note-attachments/${noteId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getNoteAttachments(noteId: number): Promise<NoteAttachment[]> {
    try {
      const response = await this.client.get(`/note-attachments/${noteId}`);
      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  async deleteNoteAttachment(attachmentId: number): Promise<void> {
    try {
      await this.client.delete(`/note-attachments/${attachmentId}`);
    } catch (error: any) {
      throw error;
    }
  }

  async processAttachment(attachmentId: number, generateInsights: boolean = true): Promise<DocumentProcessingResult> {
    try {
      const response = await this.client.post(`/note-attachments/${attachmentId}/process`, null, {
        params: { generate_insights: generateInsights }
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async appendInsightsToNote(attachmentId: number): Promise<AppendInsightsResult> {
    try {
      const response = await this.client.post(`/note-attachments/${attachmentId}/append-insights`);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  getNoteAttachmentDownloadUrl(attachmentId: number): string {
    return `${this.client.defaults.baseURL}/note-attachments/${attachmentId}/download`;
  }

  // === ID/IP Lookup API ===

  /**
   * Search location data by registration IDs
   * @param registrationIds Array of device registration IDs
   * @param dateRange Start and end dates for the search
   */
  async searchByRegistrationIds(params: {
    registrationIds: string[];
    dateRange: [Date, Date];
  }): Promise<any> {
    try {
      
      const startDateFormatted = params.dateRange[0].toISOString().replace(/\.\d{3}Z$/, 'Z');
      const endDateFormatted = params.dateRange[1].toISOString().replace(/\.\d{3}Z$/, 'Z');
      
      const payload = {
        registration_ids: params.registrationIds,
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        response_type: 'DIRECT'
      };

      
      const response = await this.client.post('/geoneutron/location-data/search', payload);
      
      // GeoNeutron returns data in 'locationData' field which is an object
      // containing registrationIDs array, each with a signals array
      const data = response.data;
      
      // Extract signals from locationData.registrationIDs array
      // Structure: locationData.registrationIDs[].signals[]
      if (data.locationData?.registrationIDs && Array.isArray(data.locationData.registrationIDs)) {
        const allSignals: any[] = [];
        data.locationData.registrationIDs.forEach((regIdObj: any) => {
          if (regIdObj.signals && Array.isArray(regIdObj.signals)) {
            regIdObj.signals.forEach((signal: any) => {
              allSignals.push({ 
                ...signal, 
                registrationID: regIdObj.registrationID || regIdObj.registrationId 
              });
            });
          }
        });
        data.locations = allSignals;
      }
      
      return data;
    } catch (error: any) {
      throw new Error(`ID lookup failed: ${error.response?.data?.detail || error.message}`);
    }
  }

  /**
   * Search location data by IP addresses
   * @param ipAddresses Array of IP addresses
   * @param dateRange Start and end dates for the search
   */
  async searchByIPAddresses(params: {
    ipAddresses: string[];
    dateRange: [Date, Date];
  }): Promise<any> {
    try {
      
      const startDateFormatted = params.dateRange[0].toISOString().replace(/\.\d{3}Z$/, 'Z');
      const endDateFormatted = params.dateRange[1].toISOString().replace(/\.\d{3}Z$/, 'Z');
      
      const payload = {
        ip_addresses: params.ipAddresses,
        start_date: startDateFormatted,
        end_date: endDateFormatted
      };

      
      const response = await this.client.post('/geoneutron/ip-address/location-data', payload);
      
      // GeoNeutron returns data in 'ipAddresses' field for IP lookups
      const data = response.data;
      
      // Normalize response - GeoNeutron uses 'ipAddresses' field which contains the data
      // Each ipAddress object may have signals array or the data might be structured differently
      if (data.ipAddresses && Array.isArray(data.ipAddresses)) {
        
        // Flatten all signals from all IP addresses into locations array
        const allSignals: any[] = [];
        data.ipAddresses.forEach((ipObj: any) => {
          
          // Check for signals array
          if (ipObj.signals && Array.isArray(ipObj.signals)) {
            ipObj.signals.forEach((signal: any) => {
              allSignals.push({ ...signal, ipAddress: ipObj.ipAddress });
            });
          }
          // Check for registrationIDs array (same structure as ID lookup)
          else if (ipObj.registrationIDs && Array.isArray(ipObj.registrationIDs)) {
            ipObj.registrationIDs.forEach((regIdObj: any) => {
              if (regIdObj.signals && Array.isArray(regIdObj.signals)) {
                regIdObj.signals.forEach((signal: any) => {
                  allSignals.push({ ...signal, ipAddress: ipObj.ipAddress, registrationID: regIdObj.registrationID });
                });
              }
            });
          }
          // Check for locationData (similar to ID lookup structure)
          else if (ipObj.locationData?.registrationIDs) {
            ipObj.locationData.registrationIDs.forEach((regIdObj: any) => {
              if (regIdObj.signals && Array.isArray(regIdObj.signals)) {
                regIdObj.signals.forEach((signal: any) => {
                  allSignals.push({ ...signal, ipAddress: ipObj.ipAddress, registrationID: regIdObj.registrationID });
                });
              }
            });
          }
          // If the ipObj itself has location data fields, treat it as a single signal
          else if (ipObj.latitude && ipObj.longitude) {
            allSignals.push({ ...ipObj });
          }
        });
        data.locations = allSignals;
      }
      
      return data;
    } catch (error: any) {
      throw new Error(`IP lookup failed: ${error.response?.data?.detail || error.message}`);
    }
  }

  // =============================================================================
  // Critical Infrastructure API
  // =============================================================================

  /**
   * Check if a single point is near critical infrastructure
   */
  async checkInfrastructureProximity(params: {
    lat: number;
    lon: number;
    radius_meters?: number;
    types?: string[];
  }): Promise<{
    found: boolean;
    count: number;
    infrastructure: Array<{
      id: number;
      name: string;
      type: string;
      icon: string;
      iata?: string;
      icao?: string;
      distance_meters: number;
      distance_km: number;
      country?: string;
      city?: string;
      priority: number;
    }>;
  }> {
    try {
      const response = await this.client.post('/infrastructure/check', {
        lat: params.lat,
        lon: params.lon,
        radius_meters: params.radius_meters || 5000,
        types: params.types
      });
      return response.data;
    } catch (error: any) {
      console.error('Infrastructure proximity check failed:', error);
      return { found: false, count: 0, infrastructure: [] };
    }
  }

  /**
   * Batch check multiple points for infrastructure proximity
   */
  async checkInfrastructureProximityBatch(params: {
    points: Array<{ id?: string; lat: number; lon: number }>;
    radius_meters?: number;
    types?: string[];
  }): Promise<{
    total_checked: number;
    points_near_infrastructure: number;
    results: Array<{
      point_id: string;
      lat: number;
      lon: number;
      found: boolean;
      infrastructure: Array<{
        id: number;
        name: string;
        type: string;
        icon: string;
        distance_meters: number;
      }>;
    }>;
  }> {
    try {
      const response = await this.client.post('/infrastructure/check-batch', {
        points: params.points,
        radius_meters: params.radius_meters || 5000,
        types: params.types
      });
      return response.data;
    } catch (error: any) {
      console.error('Infrastructure batch proximity check failed:', error);
      return { total_checked: 0, points_near_infrastructure: 0, results: [] };
    }
  }

  /**
   * Get infrastructure statistics
   */
  async getInfrastructureStats(): Promise<{
    status: string;
    data: {
      total: number;
      by_type: Record<string, number>;
      countries_by_type: Record<string, number>;
    };
  }> {
    try {
      const response = await this.client.get('/infrastructure/stats');
      return response.data;
    } catch (error: any) {
      console.error('Failed to get infrastructure stats:', error);
      return { status: 'error', data: { total: 0, by_type: {}, countries_by_type: {} } };
    }
  }
}

export const pathfinderApi = new PathfinderApiService();
export default pathfinderApi;
